import { createFileRoute } from '@tanstack/react-router'
import { ShopDetailPage } from '~/features/shop-detail/components/ShopDetailPage'
import { shopDetailQueryOptions } from '~/features/shop-detail/hooks/use-shop-detail'
import { seo } from '~/utils/seo'
import { EDGE_CACHE_HEADERS } from '~/utils/cache'
import type { QueryClient } from '@tanstack/react-query'
import { defaultLocale } from '~/lib/i18n/shared'

function getFallbackSeoText(locale: string) {
  switch (locale) {
    case 'zh-CN': return { title: '未找到餐厅 | TabeTabi', description: '无法找到请求的餐厅。' }
    case 'zh-TW': return { title: '未找到餐廳 | TabeTabi', description: '無法找到請求的餐廳。' }
    case 'ja': return { title: 'レストランが見つかりません | TabeTabi', description: 'リクエストされたレストランは見つかりませんでした。' }
    case 'ko': return { title: '레스토랑을 찾을 수 없음 | TabeTabi', description: '요청한 레스토랑을 찾을 수 없습니다.' }
    default: return { title: 'Shop Not Found | TabeTabi', description: 'The requested shop could not be found.' }
  }
}

function getLocalizedDescriptionFallback(locale: string, name: string, category: string, rating: string) {
  switch (locale) {
    case 'zh-CN': return `发现 ${name}，这是一家评价极高的${category}，评分为 ${rating}。`
    case 'zh-TW': return `發現 ${name}，這是一家評價極高的${category}，評分為 ${rating}。`
    case 'ja': return `評価 ${rating} の人気の${category}「${name}」を発見しましょう。`
    case 'ko': return `평점 ${rating}의 인기 ${category} '${name}'을(를) 만나보세요.`
    default: return `Discover ${name}, a top-rated ${category} with a rating of ${rating}.`
  }
}

export const Route = createFileRoute('/{-$locale}/_public/shops/$shopId')({
  loader: async ({ context, params }) => {
    const queryClient = context.queryClient as QueryClient
    const locale = (params as any).locale || defaultLocale
    try {
      const shop = await queryClient.ensureQueryData(shopDetailQueryOptions(params.shopId, locale))
      return { shop }
    } catch (e) {
      return { shop: null }
    }
  },
  headers: () => EDGE_CACHE_HEADERS,
  head: ({ loaderData, params }) => {
    const shop = loaderData?.shop
    const locale = (params as any).locale || defaultLocale

    if (!shop) {
      const fallback = getFallbackSeoText(locale)
      return seo({
        title: fallback.title,
        description: fallback.description,
        pathname: `/shops/${params.shopId}`,
        locale,
      })
    }

    const title = `${shop.translation?.name || shop.translation?.alias || 'Restaurant'} - ${shop.translation?.category || 'Restaurant'} | TabeTabi`
    const name = shop.translation?.name || shop.translation?.alias || 'Restaurant'
    const category = shop.translation?.category || 'restaurant'
    const rating = shop.ratingScore ? String(shop.ratingScore) : 'N/A'
    
    const description = shop.translation?.description 
      ? (shop.translation.description.length > 150 ? shop.translation.description.slice(0, 147) + '...' : shop.translation.description)
      : getLocalizedDescriptionFallback(locale, name, category, rating)

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Restaurant',
      'name': shop.translation?.name || shop.translation?.alias || 'Restaurant',
      'image': shop.imageUrl ? [shop.imageUrl] : [],
      'address': shop.translation?.address ? {
        '@type': 'PostalAddress',
        'streetAddress': shop.translation.address
      } : undefined,
      'aggregateRating': shop.ratingScore ? {
        '@type': 'AggregateRating',
        'ratingValue': shop.ratingScore,
        'reviewCount': shop.reviewCount || 1,
        'bestRating': '5',
        'worstRating': '1'
      } : undefined,
      'servesCuisine': shop.translation?.category,
      'url': `https://tabetabi.cc/${locale}/shops/${params.shopId}`
    }

    const { meta, links } = seo({
      title,
      description,
      image: shop.imageUrl || undefined,
      pathname: `/shops/${params.shopId}`,
      locale,
    })

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(schema)
        }
      ]
    }
  },
  component: ShopDetailPage,
})
